import { API, graphqlOperation } from "aws-amplify";
import { Observable } from "zen-observable-ts";

// AppSync configuration
export const APPSYNC_CONFIG = {
    aws_appsync_graphqlEndpoint: process.env.NEXT_PUBLIC_APPSYNC_GRAPHQL_ENDPOINT || "",
    aws_appsync_region: "eu-central-1",
    aws_appsync_authenticationType: "API_KEY",
    aws_appsync_apiKey: process.env.NEXT_PUBLIC_APPSYNC_API_KEY || ""
};

// Initialize the API with the combined configuration
const initializeAPI = () => {

    API.configure(APPSYNC_CONFIG);
};

// Call this once when the app starts
initializeAPI();

// Article subscription query
export const ARTICLE_STATUS_SUBSCRIPTION = `
  subscription OnArticleStatusChanged($id: ID!) {
    onArticleStatusChanged(id: $id) {
      id
      status
      current
      total
      percentage
      title
      content
    }
  }
`;

// Brief subscription query
export const BRIEF_STATUS_SUBSCRIPTION = `
  subscription OnBriefStatusChanged($id: ID!) {
    onBriefStatusChanged(id: $id) {
      id
      stepRemains
      articleStatus
    }
  }
`;

// Optimization content subscription query
export const OPTIMIZATION_STATUS_SUBSCRIPTION = `
  subscription OnOptimizationStatusChanged($id: ID!) {
    onOptimizationStatusChanged(id: $id) {
      id
      stage
      percentage
      status
    }
  }
`;

// Types
export type ArticleProgressType = {
    status: "processing" | "completed" | "failed";
    current: number;
    total: number;
    percentage: number;
};

export type BriefProgressType = {
    stepRemains: number;
    articleStatus: string;
};

export type OptimizationProgressType = {
    stage: string;
    status: string;
    percentage: number;
};



// Function to subscribe to article status changes
export const subscribeToArticleStatus = (
    briefId: string,
    onData: (data: any) => void,
    onError: (error: any) => void,
    onComplete: () => void
) => {
    try {

        const subscription = (
            API.graphql(
                graphqlOperation(ARTICLE_STATUS_SUBSCRIPTION, {
                    id: briefId,
                })
            ) as Observable<any>
        ).subscribe({
            next: (data) => {
                onData(data);
            },
            error: (error) => {
                onError(error);
            },
            complete: () => {
                onComplete();
            },
        });

        return subscription;
    } catch (error) {
        console.error(`Error setting up article subscription for brief ${briefId}:`, error);
        onError(error);
        return null;
    }
};

// Function to subscribe to brief status changes - using a dedicated API instance
export const subscribeToBriefStatus = (
    briefId: string,
    onData: (data: any) => void,
    onError: (error: any) => void,
    onComplete: () => void
) => {
    try {

        const subscription = (
            API.graphql(
                graphqlOperation(BRIEF_STATUS_SUBSCRIPTION, {
                    id: briefId,
                })
            ) as Observable<any>
        ).subscribe({
            next: (data) => {
                onData(data);
            },
            error: (error) => {
                onError(error);
            },
            complete: () => {
                onComplete();
            },
        });

        return subscription;
    } catch (error) {
        console.error(`Error setting up brief subscription for brief ${briefId}:`, error);
        onError(error);
        return null;
    }
};

// Function to subscribe to optimization status changes - using a dedicated API instance
export const subscribeToOptimizationStatus = (
    articleId: string,
    onData: (data: any) => void,
    onError: (error: any) => void,
    onComplete: () => void
) => {
    try {

        const subscription = (
            API.graphql(
                graphqlOperation(OPTIMIZATION_STATUS_SUBSCRIPTION, {
                    id: articleId,
                })
            ) as Observable<any>
        ).subscribe({
            next: (data) => {
                onData(data);
            },
            error: (error) => {
                onError(error);
            },
            complete: () => {
                onComplete();
            },
        });

        return subscription;
    } catch (error) {
        console.error(`Error setting up optimization subscription for article ${articleId}:`, error);
        onError(error);
        return null;
    }
};

// Helper to clean up subscriptions
export const cleanupSubscriptions = (subscriptionsMap: Map<string, any>) => {
    if (subscriptionsMap.size > 0) {
        subscriptionsMap.forEach((subscription, id) => {

            subscription.unsubscribe();
        });
        subscriptionsMap.clear();
    }
};

// ============================================================
// Bulk Brief Cell Update Subscription (Collaborative Editing)
// ============================================================

// Cell update subscription query
export const BULK_BRIEF_CELL_UPDATE_SUBSCRIPTION = `
  subscription OnBulkBriefCellUpdate($clientId: ID!, $sheetId: ID!) {
    onBulkBriefCellUpdate(clientId: $clientId, sheetId: $sheetId) {
      clientId
      sheetId
      rowId
      field
      value
      updatedBy
      timestamp
      rowVersion
    }
  }
`;

// Type for cell update events
export type CellUpdateType = {
    clientId: string;
    sheetId: string;
    rowId: string;
    field: string;
    value: any;
    updatedBy: string;
    timestamp: number;
    rowVersion?: number;
};

/**
 * Subscribe to bulk brief cell updates for collaborative editing
 *
 * @param clientId - The client ID to filter updates
 * @param sheetId - The sheet ID to filter updates
 * @param onData - Callback when a cell update is received
 * @param onError - Callback when an error occurs
 * @param onComplete - Callback when subscription completes (optional)
 * @returns Subscription object with unsubscribe method
 */
export const subscribeToBulkBriefCellUpdates = (
    clientId: string,
    sheetId: string,
    onData: (data: CellUpdateType) => void,
    onError: (error: any) => void,
    onComplete?: () => void
) => {
    try {
        console.log(`[AppSync] Setting up cell update subscription for ${clientId}/${sheetId}`);

        const subscription = (
            API.graphql(
                graphqlOperation(BULK_BRIEF_CELL_UPDATE_SUBSCRIPTION, {
                    clientId,
                    sheetId,
                })
            ) as Observable<any>
        ).subscribe({
            next: (response) => {
                const cellUpdate = response?.value?.data?.onBulkBriefCellUpdate;
                if (cellUpdate) {
                    // Parse the AWSJSON value back to its original type
                    let parsedValue = cellUpdate.value;
                    if (typeof cellUpdate.value === 'string') {
                        try {
                            parsedValue = JSON.parse(cellUpdate.value);
                        } catch (e) {
                            // If parsing fails, use the raw value
                        }
                    }
                    const parsedUpdate = {
                        ...cellUpdate,
                        value: parsedValue
                    };
                    onData(parsedUpdate);
                }
            },
            error: (error) => {
                console.error(`[AppSync] Cell update subscription error:`, error);
                onError(error);
            },
            complete: () => {
                console.log(`[AppSync] Cell update subscription completed`);
                if (onComplete) {
                    onComplete();
                }
            },
        });

        return subscription;
    } catch (error) {
        console.error(`[AppSync] Error setting up cell update subscription:`, error);
        onError(error);
        return null;
    }
};